import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { 
  Clock, Trophy, TrendingUp, Users, Zap, Shield, 
  BarChart3, Star, Palette, Crown, ArrowRight
} from "lucide-react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { trackEvent } from "@/lib/analytics";

const Features = () => {
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent('features_page_viewed');
  }, []);

  const categories = [
    {
      title: "Track & Measure",
      description: "Everything you need to understand and improve your study habits",
      features: [
        {
          icon: Clock,
          title: "Smart Study Timer",
          description: "Choose from preset Forge Cycles (30/45/60/90 min) or create unlimited custom timers with Premium.",
          tier: "Free + Premium",
          highlights: ["Preset timers", "Break recommendations", "Anti-cheat protection"]
        },
        {
          icon: TrendingUp,
          title: "Progress Analytics",
          description: "Track your journey with real-time streaks, daily/weekly/monthly stats, and lifetime graphs.",
          tier: "Free + Premium",
          highlights: ["Real-time streaks", "Session history", "Lifetime graphs"]
        },
        {
          icon: BarChart3,
          title: "Advanced Analytics",
          description: "Deep-dive into study patterns with time heatmaps, category breakdowns, and exportable reports.",
          tier: "Premium",
          highlights: ["Time heatmaps", "Category tracking", "PDF/CSV export"]
        }
      ]
    },
    {
      title: "Compete & Connect",
      description: "Turn studying into a social experience with friends and global rankings",
      features: [
        {
          icon: Trophy,
          title: "Global Leaderboards",
          description: "Compete with learners worldwide. Free users get day/week views. Premium unlocks month/year and streak rankings.",
          tier: "Free + Premium",
          highlights: ["Global rankings", "Multiple timeframes", "Percentile rankings"]
        },
        {
          icon: Users,
          title: "Friends System",
          description: "Follow friends, view their stats, and compete in private leaderboards.",
          tier: "Free",
          highlights: ["Follow friends", "Private rankings", "Activity feed"]
        },
        {
          icon: Zap,
          title: "XP & Prestige System",
          description: "Earn XP through study and progress from Apprentice I to Legendary Master V.",
          tier: "Free",
          highlights: ["XP per session", "Prestige tiers", "Achievement badges"]
        }
      ]
    },
    {
      title: "Premium Perks",
      description: "Unlock the full power of StudyForge with exclusive features",
      features: [
        {
          icon: Shield,
          title: "Streak Protection",
          description: "Never lose your streak on busy days. Get 2 freeze days per month to protect your progress.",
          tier: "Premium",
          highlights: ["2 freeze days/month", "Auto-protection", "Streak history"]
        },
        {
          icon: Star,
          title: "Break Skip Passes",
          description: "Need to power through? Get 2 break skip passes per week without penalties.",
          tier: "Premium",
          highlights: ["2 passes/week", "No penalties", "Auto-reset weekly"]
        },
        {
          icon: Palette,
          title: "Animated Avatars",
          description: "Stand out with exclusive animated avatars and accessories.",
          tier: "Premium",
          highlights: ["Animated avatars", "Exclusive accessories", "Premium themes"]
        }
      ]
    }
  ];

  const comparisonData = [
    { feature: "Preset Study Timers", free: true, premium: true },
    { feature: "Unlimited Custom Timers", free: false, premium: true },
    { feature: "Global Leaderboards", free: true, premium: true },
    { feature: "Friends System", free: true, premium: true },
    { feature: "XP & Prestige System", free: true, premium: true },
    { feature: "Basic Analytics", free: true, premium: true },
    { feature: "Advanced Analytics Dashboard", free: false, premium: true },
    { feature: "Streak Protection (2/month)", free: false, premium: true },
    { feature: "Break Skip Passes (2/week)", free: false, premium: true },
    { feature: "Daily Study Limit", free: "8 hours", premium: "Unlimited" },
    { feature: "Leaderboard Day/Week Views", free: true, premium: true },
    { feature: "Leaderboard Month/Year + Streak", free: false, premium: true },
    { feature: "Animated Avatars", free: false, premium: true },
    { feature: "Priority Support", free: false, premium: true },
  ];

  return (
    <div className="min-h-screen bg-background charcoal-pro">
      <MarketingHeader />

      {/* Hero */}
      <section className="container mx-auto px-4 py-12 md:py-16 text-center">
        <p className="text-lg text-primary font-medium tracking-wide mb-4">
          Made for consistency. Built for enjoyment.
        </p>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          Every Feature You Need to Stay Focused
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
          From smart timers to global leaderboards, StudyForge has everything to make studying engaging and rewarding.
        </p>
      </section>

      {/* Why Go Premium Callout */}
      <section className="container mx-auto px-4 pb-8">
        <Card className="max-w-4xl mx-auto p-6 md:p-8 border-primary bg-primary/5">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Crown className="w-8 h-8 text-primary" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Serious About Consistency? Go Premium.
              </h2>
              <p className="text-muted-foreground mb-4">
                Unlock streak protection, unlimited study time, custom timers, and advanced analytics — all for less than a coffee per week.
              </p>
              <ul className="grid grid-cols-2 gap-2 text-sm">
                <li className="flex items-center gap-2 text-foreground">
                  <Shield className="w-4 h-4 text-primary" />
                  Never lose your streak
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Clock className="w-4 h-4 text-primary" />
                  Unlimited study time
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Deep analytics
                </li>
                <li className="flex items-center gap-2 text-foreground">
                  <Palette className="w-4 h-4 text-primary" />
                  Exclusive cosmetics
                </li>
              </ul>
            </div>
            <div className="flex-shrink-0">
              <Button size="lg" onClick={() => {
                trackEvent('premium_cta_clicked', { source: 'features_callout' });
                navigate('/pricing');
              }}>
                Just $5.99/mo
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </section>

      {/* Feature Categories */}
      {categories.map((category, catIndex) => (
        <section key={catIndex} className={`py-12 ${catIndex % 2 === 1 ? 'bg-secondary/30' : ''}`}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {category.title}
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                {category.description}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {category.features.map((feature, featureIndex) => (
                <Card 
                  key={featureIndex} 
                  className={`p-6 hover:shadow-lg transition-shadow ${
                    feature.tier === "Premium" ? 'border-primary/50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <feature.icon className="w-10 h-10 text-primary" />
                    <span className={`text-xs px-2 py-1 rounded ${
                      feature.tier === "Premium" 
                        ? "bg-primary/10 text-primary font-medium" 
                        : "bg-secondary text-secondary-foreground"
                    }`}>
                      {feature.tier}
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-1">
                    {feature.highlights.map((highlight, idx) => (
                      <li key={idx} className="text-sm text-muted-foreground flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-primary" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </Card>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Feature Comparison Table */}
      <section className="container mx-auto px-4 py-16 pb-24">
        <h2 className="text-3xl font-bold text-foreground text-center mb-12">
          Free vs Premium
        </h2>

        <div className="max-w-4xl mx-auto overflow-x-auto">
          {/* Desktop Table */}
          <div className="hidden md:block">
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="font-semibold text-foreground">Feature</div>
              <div className="font-semibold text-center text-foreground">Free</div>
              <div className="font-semibold text-center text-primary">Premium</div>
            </div>

            {comparisonData.map((row, index) => (
              <div key={index} className="grid grid-cols-3 gap-4 py-3 border-b border-border">
                <div className="text-muted-foreground">{row.feature}</div>
                <div className="text-center">
                  {typeof row.free === "boolean" ? (
                    row.free ? "✓" : "—"
                  ) : (
                    row.free
                  )}
                </div>
                <div className="text-center text-primary">
                  {typeof row.premium === "boolean" ? (
                    row.premium ? "✓" : "—"
                  ) : (
                    row.premium
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {comparisonData.map((row, index) => (
              <Card key={index} className="p-4">
                <div className="font-medium text-foreground mb-3">{row.feature}</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Free</div>
                    <div className="text-foreground font-medium">
                      {typeof row.free === "boolean" ? (
                        row.free ? "✓" : "—"
                      ) : (
                        row.free
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Premium</div>
                    <div className="text-primary font-medium">
                      {typeof row.premium === "boolean" ? (
                        row.premium ? "✓" : "—"
                      ) : (
                        row.premium
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <Button size="lg" onClick={() => {
            trackEvent('view_pricing_clicked', { source: 'features_comparison' });
            navigate('/pricing');
          }}>
            View Pricing Plans
          </Button>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-secondary/50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-foreground mb-4">
            Ready for Study Built for Enjoyment?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            Start with our free tier and upgrade anytime to unlock Premium features.
          </p>
          <Button size="lg" onClick={() => {
            trackEvent('footer_cta_clicked', { page: 'features' });
            navigate('/auth');
          }}>
            Get Started Free
          </Button>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default Features;
